import { type ReactNode } from "react";
import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import {
  useNavigation,
  useRoute,
  type RouteProp,
} from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardPlus,
  ExternalLink,
  FileText,
  MessageCircle,
  Plus,
  Receipt,
  Search,
  Syringe,
  X,
} from "lucide-react-native";
import {
  DialogPrimaryButton,
  SoftSecondaryButton,
  MoonPressable,
  SectionActionLink,
} from "@louise/moon-ui";
import {
  colors,
  semanticColors,
  spacing,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import CycleRecordRowActionButton from "../care/sections/CycleRecordRowActionButton";
import type { DevButtonAuditVariant, DevStackParamList } from "./DevNavigator";

type DevNav = NativeStackNavigationProp<DevStackParamList>;
type DevRoute = RouteProp<DevStackParamList, "DevButtonAudit">;

const BASE_URL = "http://localhost:8086";

const CASES: {
  variant: DevButtonAuditVariant;
  title: string;
  description: string;
}[] = [
  {
    variant: "matrix",
    title: "Button Family Matrix",
    description:
      "Current button/action families side by side. This is a review screen, not a replacement proposal.",
  },
  {
    variant: "current-routes",
    title: "Current Route Examples",
    description:
      "Reachable patient-facing screens where button/action drift is visible today.",
  },
  {
    variant: "section-actions",
    title: "Section Action Links",
    description:
      "Low-emphasis section actions such as View all, separated from row/card chevrons.",
  },
  {
    variant: "icon-buttons",
    title: "Icon Button Examples",
    description:
      "Header icons and close/remove affordances that could share an IconButton primitive.",
  },
  {
    variant: "compact-actions",
    title: "Compact Action Examples",
    description:
      "Small action buttons shown inside current product contexts before deciding on a compact action primitive.",
  },
  {
    variant: "floating-actions",
    title: "Floating Actions",
    description:
      "Floating add/chat actions are intentionally separated from normal buttons for later review.",
  },
  {
    variant: "package-level",
    title: "Package-Level / Cleanup Queue",
    description:
      "Package and old-code hits that need reachability checks before product visual review.",
  },
];

const ROUTE_ROWS: {
  label: string;
  path: string;
  usually: string;
  currentPattern: string;
  reviewAsk: string;
}[] = [
  {
    label: "Care overview",
    path: "/care",
    usually: "Care tab, active treatment surface",
    currentPattern:
      "CareReviewConceptScreen local Pressables: QuickAddButton, CalendarDateActionButton, CalendarSymptomBarButton, ConditionActionButton.",
    reviewAsk:
      "Compact action buttons and icon actions: likely a shared compact action primitive, not a tag.",
  },
  {
    label: "Documents",
    path: "/care/documents",
    usually: "Care > Documents",
    currentPattern:
      "DocumentsScreen local Pressables: floating add, search clear, upload cards, document row actions.",
    reviewAsk:
      "Separate floating action button, icon button, and regular CTA decisions.",
  },
  {
    label: "Document upload modal",
    path: "/care/documents?openUpload=true",
    usually: "Documents > add/upload document",
    currentPattern:
      "DocumentUploadModal local Pressables for option cards, close/remove controls, and footer actions.",
    reviewAsk:
      "Modal footer buttons probably align with DialogPrimaryButton/SoftSecondaryButton; option cards may be form selectors.",
  },
  {
    label: "Costs overview",
    path: "/costs",
    usually: "Costs tab overview",
    currentPattern:
      "CostsOverviewConceptScreen local Pressables and card CTA rows. This route is current even though the file lives under suggested/.",
    reviewAsk:
      "Review filled CTAs, compact estimate actions, and card action rows separately.",
  },
  {
    label: "Insurance",
    path: "/costs/insurance",
    usually: "Costs > Insurance",
    currentPattern:
      "InsuranceScreen local Pressables for verification and card actions.",
    reviewAsk: "Likely regular CTA + secondary/action-link variants.",
  },
  {
    label: "Find care",
    path: "/access/find-care",
    usually: "Access > Find Care",
    currentPattern:
      "FindCareScreen and clinic cards use local Pressables for filters, cards, and action affordances.",
    reviewAsk:
      "Card presses stay row/card behavior; explicit schedule/contact actions should be reviewed as buttons.",
  },
  {
    label: "Cryo vault",
    path: "/care/cryo-vault",
    usually: "Care > Cryo Vault",
    currentPattern:
      "CryoVaultScreen local Pressables for specimen actions, storage cards, and add controls.",
    reviewAsk: "Compact action and floating/add action families.",
  },
];

const PACKAGE_ROWS: {
  area: string;
  file: string;
  note: string;
}[] = [
  {
    area: "care package",
    file: "src/packages/care/components/AddMedicationModal.tsx",
    note: "Package-level modal, separate from the current Moon AddMedicationModal. Check imports before visual review.",
  },
  {
    area: "care package",
    file: "src/packages/care/components/AddFrozenStorageModal.tsx",
    note: "Package-level specimen/storage flow. Treat as cleanup/reachability until current app imports are confirmed.",
  },
  {
    area: "homepage package",
    file: "src/packages/homepage/sections/TreatmentProgressModal.tsx",
    note: "Old package surface with many local actions. Do not mix with current Moon route decisions until wired state is confirmed.",
  },
  {
    area: "document-vault package",
    file: "src/packages/document-vault/screens/*",
    note: "Legacy document-vault package screens. Likely cleanup/dead-code queue unless current Moon imports are found.",
  },
];

const SECTION_ACTION_ROWS = [
  {
    where: "Cycle sections",
    files:
      "CycleRelatedDocumentsSection.tsx, CycleTestsSection.tsx, ResultsLabsCard.tsx",
    current:
      "Uses ViewAllLink or SectionTitleRow; both render the shared SectionActionLink.",
  },
  {
    where: "Costs overview / payments",
    files: "CostsOverviewConceptScreen.tsx, PaymentsClaimsScreen.tsx",
    current:
      "Uses ViewAllLink or SectionTitleRow for the section-level View all action.",
  },
  {
    where: "Clinic detail / pricing",
    files: "ClinicDetailScreen.tsx, ClinicPricingCard.tsx",
    current: "Uses ViewAllLink for section-level expansion actions.",
  },
  {
    where: "You / passport",
    files: "MyPassport.tsx",
    current: "Uses ViewAllLink for the header View all action.",
  },
] as const;

const ICON_BUTTON_ROWS = [
  {
    where: "Global header",
    file: "HeaderActions.tsx",
    current:
      "SearchButton and NotificationBellButton are bare 36x36 Pressables; LanguageButton is same footprint with flag text; ProfileAvatarButton is a 32x32 avatar button.",
    suggestion:
      "Good IconButton primitive candidate for size=md, surface=bare, optional badge/dot, and avatar slot.",
  },
  {
    where: "Detail sheets",
    file: "ConditionDetailSheet.tsx, MedicationFactsheetSheet.tsx, Procedure/Test/Therapy/Supplement detail sheets",
    current:
      "Local 32x32 circular close Pressables, usually rgba(0,0,0,0.05) with X icon.",
    suggestion:
      "Good IconButton primitive candidate for surface=soft, size=sm/md.",
  },
  {
    where: "Dialog primitive",
    file: "Dialog.tsx",
    current:
      "Already has closeButton='minimal' and closeButton='with-background', but implemented locally inside Dialog.",
    suggestion:
      "Could wrap the future IconButton, keeping Dialog API unchanged.",
  },
  {
    where: "Document upload/preview",
    file: "DocumentUploadModal.tsx, DocumentPreviewModal.tsx",
    current:
      "Bare 28x28 close/remove controls using text ✕ or small X, no background.",
    suggestion:
      "Good IconButton primitive candidate for surface=bare, size=sm.",
  },
  {
    where: "Chat overlay",
    file: "ChatHubScreen.tsx",
    current: "30x30 white translucent circular back/close button with border.",
    suggestion:
      "Likely IconButton surface=glass/soft, but keep chat overlay behavior separate from tab floating action.",
  },
] as const;

const COMPACT_ACTION_ROWS = [
  {
    where: "Care overview quick add",
    file: "CareReviewConceptScreen.tsx / QuickAddButton",
    current:
      "Dark glass, 38px min-height, pill radius, white border/text, sometimes icon-only.",
    suggestion:
      "Current product action. Review as compact action button, not Tag.",
  },
  {
    where: "Care calendar actions",
    file: "CareReviewConceptScreen.tsx / CalendarDateActionButton, CalendarSymptomBarButton",
    current:
      "Dark-card action buttons with 42-48px height, 14-23px radius, very bold 12px text.",
    suggestion:
      "May need compact button variants for dark surfaces and selected/primary state.",
  },
  {
    where: "Condition detail actions",
    file: "CareReviewConceptScreen.tsx / ConditionActionButton",
    current: "Light pill, 8px vertical padding, 11.5px/800 text, icon+label.",
    suggestion:
      "Looks like compact secondary action; review before standardizing.",
  },
  {
    where: "Cycle record row actions",
    file: "CycleRecordRowActionButton.tsx",
    current: "Very small 11px/800 text, pill background, optional plus icon.",
    suggestion:
      "Could stay compact clinical action, but check readability/size in context.",
  },
  {
    where: "Payment record detail actions",
    file: "PaymentRecordDetailModal.tsx / ActionButton",
    current: "34px min-height outline/glass pill, 12px/800 text, icon+label.",
    suggestion: "Looks like compact action; probably not StatusBadge/Tag.",
  },
] as const;

function openLocalPath(path: string) {
  void Linking.openURL(`${BASE_URL}${path}`);
}

function Header({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const navigation = useNavigation<DevNav>();
  return (
    <View style={{ gap: 12 }}>
      <Pressable
        onPress={() => navigation.navigate("DevHome")}
        style={{
          alignSelf: "flex-start",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingVertical: 8,
        }}
      >
        <ChevronLeft size={16} color={colors.textMid} />
        <Text style={{ color: colors.textMid, fontSize: 13 }}>Dev</Text>
      </Pressable>
      <View style={{ gap: 6 }}>
        <Text
          style={{
            color: colors.text,
            fontSize: 28,
            fontWeight: "700",
          }}
        >
          {title}
        </Text>
        <Text style={{ color: colors.textMid, fontSize: 14, lineHeight: 20 }}>
          {description}
        </Text>
      </View>
    </View>
  );
}

function CaseNav({ active }: { active: DevButtonAuditVariant }) {
  const navigation = useNavigation<DevNav>();
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {CASES.map((item) => {
        const selected = active === item.variant;
        return (
          <Pressable
            key={item.variant}
            onPress={() =>
              navigation.navigate("DevButtonAudit", {
                variant: item.variant,
              })
            }
            style={{
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 16,
              backgroundColor: selected
                ? "rgba(59,26,8,0.12)"
                : "rgba(255,255,255,0.45)",
              borderWidth: 1,
              borderColor: selected
                ? "rgba(59,26,8,0.18)"
                : "rgba(255,255,255,0.55)",
            }}
          >
            <Text
              style={{
                color: selected ? colors.text : colors.textMid,
                fontSize: 13,
                fontWeight: selected ? "600" : "400",
              }}
            >
              {item.title}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function Shell({
  active,
  children,
}: {
  active: DevButtonAuditVariant;
  children: ReactNode;
}) {
  const item = CASES.find((entry) => entry.variant === active) ?? CASES[0]!;
  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.lg,
          paddingBottom: spacing["4xl"],
          gap: spacing.lg,
          maxWidth: 760,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Header title={item.title} description={item.description} />
        <CaseNav active={active} />
        {children}
      </ScrollView>
    </GlassScreenLayout>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <View style={{ gap: 10 }}>
      <View style={{ gap: 4 }}>
        <Text style={{ color: colors.text, fontSize: 16, fontWeight: "700" }}>
          {title}
        </Text>
        {description ? (
          <Text style={{ color: colors.textMid, fontSize: 13, lineHeight: 18 }}>
            {description}
          </Text>
        ) : null}
      </View>
      {children}
    </View>
  );
}

function ExampleCard({
  title,
  current,
  where,
  suggested,
  children,
}: {
  title: string;
  current: string;
  where: string;
  suggested: string;
  children: ReactNode;
}) {
  return (
    <View
      style={{
        gap: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(42,37,34,0.08)",
        backgroundColor: "rgba(255,255,255,0.62)",
        padding: 14,
      }}
    >
      <View style={{ minHeight: 56, justifyContent: "center" }}>
        {children}
      </View>
      <View style={{ gap: 5 }}>
        <Text style={{ color: colors.text, fontSize: 13, fontWeight: "700" }}>
          {title}
        </Text>
        <MetaLine label="Current" value={current} />
        <MetaLine label="Usually" value={where} />
        <MetaLine label="Review" value={suggested} />
      </View>
    </View>
  );
}

function MetaLine({ label, value }: { label: string; value: string }) {
  return (
    <Text style={{ color: colors.textMid, fontSize: 12, lineHeight: 16 }}>
      <Text style={{ color: colors.text, fontWeight: "700" }}>{label}: </Text>
      {value}
    </Text>
  );
}

function MatrixScreen() {
  return (
    <>
      <Section
        title="What To Review"
        description="These are visual families in current code. The question is which shared primitive names and variants should exist, not whether every pressable should become the same component."
      >
        <View style={{ gap: 12 }}>
          <ExampleCard
            title="Copper primary CTA"
            current="DialogPrimaryButton in moon-ui; WarmButton in moon-ui; several local image-background Pressables."
            where="Modal footers, coming-soon sheets, hero CTAs, treatment actions."
            suggested="Likely one shared primary button family with size/loading/icon options."
          >
            <View style={{ gap: 10 }}>
              <DialogPrimaryButton label="Confirm & save" onPress={() => {}} />
              <WarmButton title="Get notified" onPress={() => {}} />
            </View>
          </ExampleCard>

          <ExampleCard
            title="Soft secondary action"
            current="SoftSecondaryButton in moon-ui; local soft gray Pressables in modals and sheets."
            where="Cancel/back/edit actions next to a primary action."
            suggested="Likely shared secondary button, with disabled/loading support added."
          >
            <SoftSecondaryButton label="Maybe later" onPress={() => {}} />
          </ExampleCard>

          <ExampleCard
            title="Small section action link"
            current="SectionActionLink in moon-ui; ViewAllLink and SectionTitleRow wrap it for true section actions."
            where="Section headers, detail sheets, cards with right-side actions."
            suggested="Keep this separate from normal buttons. Remaining chevrons are mostly row/card navigation, not View all actions."
          >
            <View style={{ alignItems: "flex-start" }}>
              <SectionActionLink label="View all" onPress={() => {}} />
            </View>
          </ExampleCard>

          <ExampleCard
            title="Bare icon button"
            current="SearchButton, NotificationBellButton, close/remove controls, tab bar buttons are local Pressables."
            where="Headers, sheets, search inputs, notification entry, close/remove controls."
            suggested="Likely an IconButton primitive with sizes, surfaces, badge/dot, and accessibility label."
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 10 }}
            >
              <IconButtonSample
                icon={
                  <Search size={22} color={colors.textMid} strokeWidth={1.5} />
                }
              />
              <IconButtonSample
                icon={
                  <Bell size={22} color={colors.textMid} strokeWidth={1.5} />
                }
                dot
              />
              <IconButtonSample
                filled
                icon={<X size={18} color={colors.textMid} strokeWidth={1.75} />}
              />
            </View>
          </ExampleCard>

          <ExampleCard
            title="Compact clinical/action pill"
            current="CycleRecordRowActionButton and multiple local QuickAdd/ActionButton components."
            where="Cycle records, medication rows, care calendar, payment detail actions."
            suggested="Likely compact action button. It is action UI, so it should not be merged into Tag."
          >
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <CycleRecordRowActionButton label="Log dose" />
              <CycleRecordRowActionButton label="Add record" variant="add" />
              <CompactOutlineAction label="Receipt" />
            </View>
          </ExampleCard>

          <ExampleCard
            title="Floating action"
            current="Documents and chat use local floating Pressables with very different styling."
            where="Documents add action, tab chat orb, bottom actions."
            suggested="Likely a separate FAB/action-orb decision, not a normal button replacement."
          >
            <View style={{ flexDirection: "row", gap: 10 }}>
              <Pressable
                accessibilityRole="button"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 23,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.deepBrown,
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 6 },
                  shadowOpacity: 0.16,
                  shadowRadius: 14,
                }}
              >
                <Plus size={20} color="#FFFFFF" strokeWidth={2.2} />
              </Pressable>
              <MoonPressable
                accessibilityRole="button"
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 23,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.7)",
                  borderWidth: 1,
                  borderColor: "rgba(255,255,255,0.8)",
                }}
              >
                <ChevronRight size={20} color={colors.textMid} />
              </MoonPressable>
            </View>
          </ExampleCard>
        </View>
      </Section>
    </>
  );
}

function SectionActionsScreen() {
  return (
    <>
      <Section
        title="Current State"
        description="The true section-level action pattern is mostly consolidated already. ViewAllLink wraps SectionActionLink, and SectionTitleRow renders SectionActionLink when an action is passed."
      >
        <View style={{ gap: 12 }}>
          <ExampleCard
            title="Shared section action"
            current="SectionActionLink in @louise/moon-ui"
            where="View all / see more actions inside section headers"
            suggested="Keep as a low-emphasis text action. It behaves like a button, but should stay visually distinct from filled/secondary buttons."
          >
            <View style={{ gap: 12, alignItems: "flex-start" }}>
              <SectionActionLink label="View all" onPress={() => {}} />
              <SectionActionLink
                label="Open payments"
                icon={Receipt}
                onPress={() => {}}
              />
              <SectionActionLink
                label="Details"
                showChevron={false}
                onPress={() => {}}
              />
            </View>
          </ExampleCard>
          <TableRows
            rows={SECTION_ACTION_ROWS.map((row) => ({
              title: row.where,
              meta: row.files,
              body: row.current,
            }))}
          />
        </View>
      </Section>

      <Section
        title="Not The Same Pattern"
        description="The raw chevron search also finds tappable rows, cards, list items, and navigation affordances. Those should not be forced into SectionActionLink just because they have a chevron."
      >
        <Text style={{ color: colors.textMid, fontSize: 13, lineHeight: 19 }}>
          Examples include `Row`, `ListRow`, `MoonSettingRow`, card presses in
          Access/Costs/Care, and article/detail navigation rows. Those are
          row/card primitives or navigation affordances, not section header
          actions.
        </Text>
      </Section>
    </>
  );
}

function IconButtonSample({
  icon,
  dot = false,
  filled = false,
}: {
  icon: ReactNode;
  dot?: boolean;
  filled?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      style={{
        width: 36,
        height: 36,
        borderRadius: filled ? 18 : 0,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: filled
          ? semanticColors.overlay.pressed
          : "transparent",
      }}
    >
      {icon}
      {dot ? (
        <View
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 7,
            height: 7,
            borderRadius: 3.5,
            backgroundColor: colors.warm,
          }}
        />
      ) : null}
    </Pressable>
  );
}

function IconButtonsScreen() {
  return (
    <Section
      title="Examples To Review"
      description="These are the current icon-only action surfaces. The visual drift is mostly size, background surface, and whether the close icon is text or a lucide icon."
    >
      <View style={{ gap: 12 }}>
        <ExampleCard
          title="Global header actions"
          current="HeaderActions.tsx: LanguageButton, SearchButton, NotificationBellButton, ProfileAvatarButton"
          where="Top-right app header"
          suggested="IconButton can cover bare 36x36 icons, notification dot, and possibly an avatar slot."
        >
          <HeaderIconContext />
        </ExampleCard>

        <ExampleCard
          title="Close affordances"
          current="Dialog.tsx plus many local modal/sheet close buttons"
          where="Detail sheets, document modals, dialogs, chat overlay"
          suggested="IconButton can cover bare, soft, and glass close buttons while leaving Dialog's public API unchanged."
        >
          <CloseIconContext />
        </ExampleCard>

        <TableRows
          rows={ICON_BUTTON_ROWS.map((row) => ({
            title: row.where,
            meta: row.file,
            body: `${row.current} ${row.suggestion}`,
          }))}
        />
      </View>
    </Section>
  );
}

function HeaderIconContext() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: 14,
        borderRadius: 18,
        backgroundColor: "rgba(255,255,255,0.6)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.8)",
      }}
    >
      <Text style={{ color: colors.text, fontSize: 14, fontWeight: "700" }}>
        Header
      </Text>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <IconButtonSample icon={<Text style={{ fontSize: 20 }}>US</Text>} />
        <IconButtonSample
          icon={<Search size={22} color={colors.textMid} strokeWidth={1.5} />}
        />
        <IconButtonSample
          icon={<Bell size={22} color={colors.textMid} strokeWidth={1.5} />}
          dot
        />
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.45)",
            backgroundColor: "rgba(107,85,72,0.16)",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Text style={{ color: colors.greyBrown, fontWeight: "800" }}>A</Text>
        </View>
      </View>
    </View>
  );
}

function CloseIconContext() {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
      <CloseSample label="Detail sheet" surface="soft" />
      <CloseSample label="Dialog bg" surface="dialog" />
      <CloseSample label="Bare modal" surface="bare" />
      <CloseSample label="Chat overlay" surface="glass" />
    </View>
  );
}

function CloseSample({
  label,
  surface,
}: {
  label: string;
  surface: "soft" | "dialog" | "bare" | "glass";
}) {
  const isBare = surface === "bare";
  const isGlass = surface === "glass";
  return (
    <View
      style={{
        width: 132,
        minHeight: 86,
        borderRadius: 14,
        padding: 12,
        justifyContent: "space-between",
        backgroundColor: isGlass
          ? "rgba(59,26,8,0.12)"
          : "rgba(255,255,255,0.7)",
        borderWidth: 1,
        borderColor: "rgba(42,37,34,0.08)",
      }}
    >
      <View
        style={{
          alignSelf: "flex-end",
          width: surface === "bare" ? 28 : surface === "glass" ? 30 : 32,
          height: surface === "bare" ? 28 : surface === "glass" ? 30 : 32,
          borderRadius: surface === "bare" ? 14 : surface === "glass" ? 15 : 16,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor:
            surface === "soft"
              ? "rgba(0,0,0,0.05)"
              : surface === "dialog"
                ? "rgba(0,0,0,0.06)"
                : surface === "glass"
                  ? "rgba(255,255,255,0.7)"
                  : "transparent",
          borderWidth: surface === "glass" ? 1 : 0,
          borderColor: "rgba(255,255,255,0.82)",
        }}
      >
        {isGlass ? (
          <ChevronLeft size={18} color={colors.textMid} strokeWidth={2.2} />
        ) : isBare ? (
          <Text style={{ fontSize: 18, color: colors.textFaint }}>x</Text>
        ) : (
          <X size={16} color={colors.textLight} />
        )}
      </View>
      <Text style={{ color: colors.textMid, fontSize: 12, fontWeight: "700" }}>
        {label}
      </Text>
    </View>
  );
}

function CompactOutlineAction({ label }: { label: string }) {
  return (
    <Pressable
      accessibilityRole="button"
      style={{
        borderRadius: 999,
        borderWidth: 1,
        borderColor: "rgba(42,37,34,0.12)",
        paddingHorizontal: 10,
        paddingVertical: 6,
        backgroundColor: "rgba(255,255,255,0.6)",
      }}
    >
      <Text
        style={{
          color: colors.textMid,
          fontSize: 11,
          lineHeight: 13,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function CompactActionsScreen() {
  return (
    <Section
      title="In-Context Compact Actions"
      description="These are action controls in product-like contexts. They should not be converted to Tag. The question is whether a compact action button variant can cover them, or whether some are old local buttons that should be retired."
    >
      <View style={{ gap: 12 }}>
        <ExampleCard
          title="Care overview quick add"
          current="CareReviewConceptScreen.tsx / QuickAddButton"
          where="Care overview top action row"
          suggested="Likely compact action button on dark/glass surface."
        >
          <DarkCarePanel title="Quick add">
            <View style={{ flexDirection: "row", gap: 8 }}>
              <QuickAddReplica
                icon={<Syringe size={14} color="#FFF7EF" strokeWidth={2} />}
                label="Medication"
              />
              <QuickAddReplica
                icon={
                  <CalendarDays size={14} color="#FFF7EF" strokeWidth={2} />
                }
                label="Appointment"
              />
              <QuickAddReplica
                icon={<FileText size={14} color="#FFF7EF" strokeWidth={2} />}
                label="Document"
                iconOnly
              />
            </View>
          </DarkCarePanel>
        </ExampleCard>

        <ExampleCard
          title="Care calendar buttons"
          current="CareReviewConceptScreen.tsx / CalendarDateActionButton and CalendarSymptomBarButton"
          where="Care calendar / symptom bar"
          suggested="Likely compact action button with dark-surface and selected/primary states."
        >
          <DarkCarePanel title="Today">
            <View style={{ flexDirection: "row", gap: 8 }}>
              <DarkActionReplica icon={CalendarDays} label="Log dose" />
              <DarkActionReplica icon={ClipboardPlus} label="Symptom" />
            </View>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <SymptomActionReplica label="Feeling ok" active />
              <SymptomActionReplica label="Add note" />
            </View>
          </DarkCarePanel>
        </ExampleCard>

        <ExampleCard
          title="Condition detail compact actions"
          current="CareReviewConceptScreen.tsx / ConditionActionButton"
          where="Condition detail modal actions"
          suggested="Looks like compact secondary action on light surfaces."
        >
          <View
            style={{
              gap: 12,
              padding: 14,
              borderRadius: 16,
              backgroundColor: "rgba(255,255,255,0.72)",
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.08)",
            }}
          >
            <Text
              style={{ color: colors.text, fontSize: 15, fontWeight: "700" }}
            >
              Condition details
            </Text>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <LightActionReplica
                icon={<FileText size={13} color={colors.text} />}
                label="Add records"
              />
              <LightActionReplica
                icon={<X size={13} color={colors.text} />}
                label="Done"
              />
            </View>
          </View>
        </ExampleCard>

        <ExampleCard
          title="Cycle row + payment detail actions"
          current="CycleRecordRowActionButton.tsx and PaymentRecordDetailModal.tsx / ActionButton"
          where="Cycle records, medication rows, payment record detail modal"
          suggested="Probably compact action variants, but these need visual approval in context."
        >
          <View style={{ gap: 12 }}>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <CycleRecordRowActionButton label="Log dose" />
              <CycleRecordRowActionButton label="Add record" variant="add" />
              <CycleRecordRowActionButton label="Open" />
            </View>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <PaymentActionReplica icon={Receipt} label="Receipt" />
              <PaymentActionReplica icon={FileText} label="Claim" />
              <PaymentActionReplica icon={MessageCircle} label="Ask" />
            </View>
          </View>
        </ExampleCard>

        <TableRows
          rows={COMPACT_ACTION_ROWS.map((row) => ({
            title: row.where,
            meta: row.file,
            body: `${row.current} ${row.suggestion}`,
          }))}
        />
      </View>
    </Section>
  );
}

function DarkCarePanel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <View
      style={{
        gap: 12,
        borderRadius: 18,
        padding: 14,
        backgroundColor: "#3B1A08",
        borderWidth: 1,
        borderColor: "rgba(255,247,239,0.16)",
      }}
    >
      <Text style={{ color: "#FFF7EF", fontSize: 15, fontWeight: "800" }}>
        {title}
      </Text>
      {children}
    </View>
  );
}

function QuickAddReplica({
  icon,
  label,
  iconOnly = false,
}: {
  icon: ReactNode;
  label: string;
  iconOnly?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      style={{
        flexGrow: iconOnly ? 0 : 1,
        flexBasis: iconOnly ? undefined : 0,
        width: iconOnly ? 46 : undefined,
        minHeight: 38,
        borderRadius: 999,
        backgroundColor: "rgba(255,255,255,0.1)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.16)",
        paddingHorizontal: iconOnly ? 0 : 8,
        paddingVertical: 7,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        gap: iconOnly ? 4 : 5,
      }}
    >
      {icon}
      {iconOnly ? null : (
        <Text
          style={{
            color: "#FFF7EF",
            fontSize: 11.5,
            lineHeight: 14,
            fontWeight: "900",
          }}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}

function DarkActionReplica({
  icon: Icon,
  label,
}: {
  icon: typeof CalendarDays;
  label: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      style={{
        flex: 1,
        minHeight: 42,
        borderRadius: 14,
        backgroundColor: "rgba(255,247,239,0.12)",
        borderWidth: 1,
        borderColor: "rgba(255,247,239,0.17)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
      }}
    >
      <Icon size={14} color="rgba(255,247,239,0.82)" strokeWidth={2} />
      <Text
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: "#FFF7EF",
          fontWeight: "900",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SymptomActionReplica({
  label,
  active = false,
}: {
  label: string;
  active?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={{
        flex: 1,
        minHeight: 48,
        borderRadius: 23,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: active ? "#FFF7EF" : "rgba(255,247,239,0.08)",
        borderWidth: 1,
        borderColor: active
          ? "rgba(255,247,239,0.94)"
          : "rgba(255,247,239,0.12)",
      }}
    >
      <Text
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: active ? "#5A3125" : "rgba(255,247,239,0.86)",
          fontWeight: "900",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function LightActionReplica({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      style={{
        flex: 1,
        borderRadius: 999,
        paddingVertical: 8,
        paddingHorizontal: 10,
        backgroundColor: "rgba(255,255,255,0.42)",
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
      }}
    >
      {icon}
      <Text
        numberOfLines={1}
        style={{
          fontSize: 11.5,
          lineHeight: 14,
          color: colors.text,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function PaymentActionReplica({
  icon: Icon,
  label,
}: {
  icon: typeof Receipt;
  label: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      style={{
        minHeight: 34,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: "rgba(42,37,34,0.1)",
        backgroundColor: "rgba(255,255,255,0.24)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingHorizontal: 11,
      }}
    >
      <Icon size={13} color={colors.greyBrown} strokeWidth={1.85} />
      <Text
        style={{
          fontSize: 12,
          fontWeight: "800",
          color: colors.greyBrown,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function FloatingActionsScreen() {
  return (
    <Section
      title="Deferred Floating Actions"
      description="These are intentionally separate from the normal button pass. They are prominent navigation/action affordances, not ordinary CTAs."
    >
      <View style={{ gap: 12 }}>
        <ExampleCard
          title="Chat floating tab action"
          current="MainTabNavigator.tsx / floating TabButton + ChatOrbIcon"
          where="Bottom tab bar, opens chat overlay"
          suggested="Handle separately as chat/floating navigation, not primary button."
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              padding: 14,
              borderRadius: 20,
              backgroundColor: "rgba(20,20,20,0.92)",
              alignSelf: "flex-start",
            }}
          >
            <IconButtonSample
              icon={<Search size={18} color="#FFFFFF" strokeWidth={1.7} />}
            />
            <View
              style={{
                width: 58,
                height: 58,
                borderRadius: 29,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(20,20,20,0.92)",
              }}
            >
              <View
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  backgroundColor: colors.warm,
                  borderWidth: 6,
                  borderTopColor: "#B183F2",
                  borderRightColor: "#6EE7B7",
                  borderBottomColor: "#F5C882",
                  borderLeftColor: "#E8927C",
                }}
              />
            </View>
          </View>
        </ExampleCard>
        <ExampleCard
          title="Documents floating add"
          current="DocumentsScreen.tsx local floating add Pressable"
          where="Documents screen add/upload affordance"
          suggested="Handle as FAB/add action after icon buttons and core buttons."
        >
          <Pressable
            accessibilityRole="button"
            style={{
              width: 46,
              height: 46,
              borderRadius: 23,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: colors.deepBrown,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 6 },
              shadowOpacity: 0.16,
              shadowRadius: 14,
            }}
          >
            <Plus size={20} color="#FFFFFF" strokeWidth={2.2} />
          </Pressable>
        </ExampleCard>
      </View>
    </Section>
  );
}

function TableRows({
  rows,
}: {
  rows: { title: string; meta: string; body: string }[];
}) {
  return (
    <View style={{ gap: 8 }}>
      {rows.map((row) => (
        <View
          key={`${row.title}-${row.meta}`}
          style={{
            gap: 5,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: "rgba(42,37,34,0.08)",
            backgroundColor: "rgba(255,255,255,0.62)",
            padding: 12,
          }}
        >
          <Text style={{ color: colors.text, fontSize: 13, fontWeight: "700" }}>
            {row.title}
          </Text>
          <Text
            style={{ color: colors.textLight, fontSize: 11, lineHeight: 15 }}
          >
            {row.meta}
          </Text>
          <Text style={{ color: colors.textMid, fontSize: 12, lineHeight: 17 }}>
            {row.body}
          </Text>
        </View>
      ))}
    </View>
  );
}

function CurrentRoutesScreen() {
  return (
    <Section
      title="Reachable Review Links"
      description="Each row is reachable in the current Moon app. The dev screen names the current component/pattern so the review is tied back to code."
    >
      <View style={{ gap: 10 }}>
        {ROUTE_ROWS.map((row) => (
          <Pressable
            key={row.path}
            onPress={() => openLocalPath(row.path)}
            accessibilityRole="button"
            style={{
              gap: 10,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(42,37,34,0.08)",
              backgroundColor: "rgba(255,255,255,0.62)",
              padding: 14,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <View style={{ flex: 1, gap: 2 }}>
                <Text
                  style={{
                    color: colors.text,
                    fontSize: 14,
                    fontWeight: "700",
                  }}
                >
                  {row.label}
                </Text>
                <Text style={{ color: colors.textLight, fontSize: 12 }}>
                  {BASE_URL}
                  {row.path}
                </Text>
              </View>
              <ExternalLink size={16} color={colors.textLight} />
            </View>
            <MetaLine label="Usually" value={row.usually} />
            <MetaLine label="Current" value={row.currentPattern} />
            <MetaLine label="Review" value={row.reviewAsk} />
          </Pressable>
        ))}
      </View>
    </Section>
  );
}

function PackageLevelScreen() {
  return (
    <Section
      title="Do Not Review Until Reachability Is Confirmed"
      description="These are high-count button/action hits from the original issue, but they are package-level or likely legacy. They should be tagged as cleanup/reachability work before visual decisions."
    >
      <View style={{ gap: 10 }}>
        {PACKAGE_ROWS.map((row) => (
          <View
            key={row.file}
            style={{
              gap: 6,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "rgba(42,37,34,0.08)",
              backgroundColor: "rgba(255,255,255,0.62)",
              padding: 14,
            }}
          >
            <Text
              style={{ color: colors.text, fontSize: 14, fontWeight: "700" }}
            >
              {row.area}
            </Text>
            <Text
              style={{ color: colors.textMid, fontSize: 12, lineHeight: 16 }}
            >
              {row.file}
            </Text>
            <Text
              style={{ color: colors.textMid, fontSize: 12, lineHeight: 16 }}
            >
              {row.note}
            </Text>
          </View>
        ))}
      </View>
    </Section>
  );
}

export default function DevButtonAuditScreen() {
  const route = useRoute<DevRoute>();
  const active = route.params?.variant ?? "matrix";

  return (
    <Shell active={active}>
      {active === "matrix" ? <MatrixScreen /> : null}
      {active === "current-routes" ? <CurrentRoutesScreen /> : null}
      {active === "section-actions" ? <SectionActionsScreen /> : null}
      {active === "icon-buttons" ? <IconButtonsScreen /> : null}
      {active === "compact-actions" ? <CompactActionsScreen /> : null}
      {active === "floating-actions" ? <FloatingActionsScreen /> : null}
      {active === "package-level" ? <PackageLevelScreen /> : null}
    </Shell>
  );
}
